import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlayIcon, XIcon } from 'lucide-react';
import { SectionHeading } from '../components/SectionHeading';
import { useLanguage } from '../contexts/LanguageContext';
import { photos, reels, snippets, filterKeys, type FilterKey, type Photo } from '../data/media';
import { mediaText } from '../data/mediaText';
import { ui } from '../data/ui';
export function Portfolio() {
  const {
    language
  } = useLanguage();
  const strings = ui[language];
  const text = mediaText[language];
  const [filter, setFilter] = useState<FilterKey>('all');
  const [lightbox, setLightbox] = useState<Photo | null>(null);
  const visiblePhotos = useMemo(() => filter === 'all' ? photos : photos.filter(photo => photo.category === filter), [filter]);
  return <div className="w-full bg-blush-100" data-id="element-125" data-mp="198rnrwrnse1j">
      <section className="mx-auto max-w-6xl px-5 pb-10 pt-12 sm:px-8 sm:pt-16" data-id="element-126" data-mp="156o10bzjhn0y">
        <p className="text-xs uppercase tracking-[0.3em] text-ink/50" data-id="element-127" data-mp="158qvp9bn04c0">
          {strings.portfolio.eyebrow}
        </p>
        <h1 className="mt-3 font-script text-5xl text-ink sm:text-6xl" data-id="element-128" data-mp="1b8x8gzigqb7e">
          {strings.portfolio.title}
        </h1>
        <div className="mt-5 h-1.5 w-full bg-blush-300" data-id="element-129" data-mp="1g801tmp2yszj" />
        <p className="mt-5 max-w-2xl text-sm leading-relaxed text-ink/75 sm:text-base" data-id="element-130" data-mp="1g6gl5uziifx8">
          {strings.portfolio.intro}
        </p>
      </section>

      {/* Photos */}
      <section className="mx-auto max-w-6xl px-5 pb-16 sm:px-8" data-id="element-131" data-mp="1pxjl2tgpgoux">
        <SectionHeading data-id="element-132" data-mp="1tywu1cf3h862">{strings.sections.photos}</SectionHeading>

        <div className="mt-6 flex flex-wrap gap-2" role="tablist" aria-label={strings.portfolio.filterLabel} data-id="element-133" data-mp="1yyrk5dr1ptq2">
          {filterKeys.map(key => {
          const active = key === filter;
          return <button key={key} type="button" role="tab" aria-selected={active} onClick={() => setFilter(key)} className={`rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] transition-colors ${active ? 'bg-ink text-blush-50' : 'border border-ink/20 text-ink/60 hover:bg-white hover:text-ink'}`} data-id="element-134" data-mp="4m1nwpd7g8fi">
                {text.categories[key]}
              </button>;
        })}
        </div>

        <motion.ul layout className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-135" data-mp="kgg9yvnnb80e">
          <AnimatePresence mode="popLayout" data-id="element-136" data-mp="gfcrs2i4kzw0">
            {visiblePhotos.map(photo => {
            const meta = text.photos[photo.id];
            return <motion.li key={photo.id} layout initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.35,
              ease: 'easeOut'
            }} className={photo.span ? 'sm:col-span-2' : undefined} data-id="element-137" data-mp="p8zopvy2k96s">
                  <button type="button" onClick={() => setLightbox(photo)} className="group block w-full overflow-hidden bg-white text-left" data-id="element-138" data-mp="1mualf1kpvtbt">
                    <span className="block overflow-hidden" data-id="element-139" data-mp="1dt2ytgrkcgxk">
                      <img src={photo.src} alt={meta.alt} loading="lazy" className={`w-full object-cover transition-transform duration-500 group-hover:scale-[1.04] ${photo.span ? 'aspect-[16/10]' : 'aspect-[4/5]'}`} data-id="element-140" data-mp="1dvfgrtxbmram" />
                    </span>
                    <span className="flex items-baseline justify-between gap-3 px-4 py-3" data-id="element-141" data-mp="g5u897cpghop">
                      <span className="text-sm font-medium text-ink" data-id="element-142" data-mp="g86rroy4303v">{meta.caption}</span>
                      <span className="text-[10px] uppercase tracking-[0.2em] text-ink/45" data-id="element-143" data-mp="l71kbilvs316">
                        {text.categories[photo.category]}
                      </span>
                    </span>
                  </button>
                </motion.li>;
          })}
          </AnimatePresence>
        </motion.ul>
      </section>

      {/* Reels */}
      <section className="bg-blush-200/60 py-16" data-id="element-144" data-mp="19q59drtkml95">
        <div className="mx-auto max-w-6xl px-5 sm:px-8" data-id="element-145" data-mp="14rlozvumlhiw">
          <SectionHeading variant="plain" data-id="element-146" data-mp="14teu36plbtos">{strings.sections.reels}</SectionHeading>
          <p className="mt-4 max-w-2xl text-sm text-ink/70" data-id="element-147" data-mp="10pgy34vkayzy">{strings.portfolio.reelsIntro}</p>
          <ul className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-148" data-mp="vs8gp6i8myzr">
            {reels.map(reel => {
            const meta = text.reels[reel.id];
            return <li key={reel.id} data-id="element-149" data-mp="2q5gcb0e1femg">
                  <article className="group relative overflow-hidden bg-ink" data-id="element-150" data-mp="2q4oj1w7wxszc">
                    <img src={reel.poster} alt="" loading="lazy" aria-hidden="true" className="aspect-[9/16] w-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-[1.03]" data-id="element-151" data-mp="2l750ik46mu1l" />
                    <div className="absolute inset-0 flex flex-col justify-end bg-ink/25 p-5" data-id="element-152" data-mp="32hiqtilxcno4">
                      <span className="mb-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-blush-50/90 text-ink transition-transform duration-300 group-hover:scale-110" data-id="element-153" data-mp="37dr4ssodyatp">
                        <PlayIcon className="ml-0.5 h-5 w-5" aria-hidden="true" data-id="element-154" data-mp="2eq3m5j6odu9p" />
                      </span>
                      <h3 className="text-base font-semibold text-blush-50" data-id="element-155" data-mp="2jr135sfy8i9m">{meta.title}</h3>
                      <p className="mt-1 text-xs uppercase tracking-[0.2em] text-blush-50/75" data-id="element-156" data-mp="2opc8bpvau4li">
                        {meta.meta}
                      </p>
                    </div>
                  </article>
                </li>;
          })}
          </ul>
        </div>
      </section>

      {/* Snippets */}
      <section className="mx-auto max-w-6xl px-5 py-16 sm:px-8" data-id="element-157" data-mp="1v1u86vfgsx8q">
        <SectionHeading data-id="element-158" data-mp="1v1c5ph27lmsd">{strings.sections.snippets}</SectionHeading>
        <ul className="mt-8 space-y-6" data-id="element-159" data-mp="291winwlg0ody">
          {snippets.map(snippet => {
          const meta = text.snippets[snippet.id];
          return <motion.li key={snippet.id} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.45,
            ease: 'easeOut'
          }} data-id="element-160" data-mp="24y70ppz9x51w">
                <article className="grid gap-5 bg-white p-4 sm:grid-cols-[260px_1fr] sm:items-center" data-id="element-161" data-mp="1q4lourikyn5w">
                  <div className="relative overflow-hidden" data-id="element-162" data-mp="1la7qcg8mfzx0">
                    <img src={snippet.poster} alt="" loading="lazy" aria-hidden="true" className="aspect-video w-full object-cover" data-id="element-163" data-mp="xkwu9c27utp7" />
                    <span className="absolute inset-0 flex items-center justify-center bg-ink/20" data-id="element-164" data-mp="o867kw96xb22">
                      <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-blush-50/90 text-ink" data-id="element-165" data-mp="9j9x6sjnvlya">
                        <PlayIcon className="ml-0.5 h-5 w-5" aria-hidden="true" data-id="element-166" data-mp="eimiv9ysqmza" />
                      </span>
                    </span>
                  </div>
                  <div data-id="element-167" data-mp="1768riwmvsht0">
                    <h3 className="text-base font-bold uppercase tracking-wide text-ink" data-id="element-168" data-mp="175h5odj35yfx">
                      {meta.title}
                    </h3>
                    <p className="mt-1 text-xs uppercase tracking-[0.2em] text-ink/50" data-id="element-169" data-mp="sblydbykwh6u">
                      {meta.meta}
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-ink/75" data-id="element-170" data-mp="3cyfevzjivcak">
                      {meta.description}
                    </p>
                  </div>
                </article>
              </motion.li>;
        })}
        </ul>
      </section>

      {/* Lightbox */}
      <AnimatePresence data-id="element-171" data-mp="2u8oc9v7pzims">
        {lightbox && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-ink/85 p-4" role="dialog" aria-modal="true" aria-label={text.photos[lightbox.id].caption} onClick={() => setLightbox(null)} data-id="element-172" data-mp="2ub0u88dh9szu">
            <button type="button" onClick={() => setLightbox(null)} aria-label={strings.portfolio.closeLabel} className="absolute right-5 top-5 rounded-full bg-blush-50/90 p-2 text-ink" data-id="element-173" data-mp="3d07c5t6g6gkl">
              <XIcon className="h-5 w-5" data-id="element-174" data-mp="4jvqttbe1ik0" />
            </button>
            <motion.figure initial={{
          scale: 0.96,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.96,
          opacity: 0
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="max-h-full w-full max-w-3xl" onClick={event => event.stopPropagation()} data-id="element-175" data-mp="3vybbxx0g339q">
              <img src={lightbox.src} alt={text.photos[lightbox.id].alt} className="max-h-[75vh] w-full object-contain" data-id="element-176" data-mp="1resert82lae3" />
              <figcaption className="mt-3 text-center text-sm uppercase tracking-[0.2em] text-blush-50" data-id="element-177" data-mp="21dqabg3wy46l">
                {text.photos[lightbox.id].caption}
              </figcaption>
            </motion.figure>
          </motion.div>}
      </AnimatePresence>
    </div>;
}